import { z } from 'zod';

import { GapUnit } from '@/models/appointment/AppointmentChainConfig.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';
import { InsuranceType } from '@/models/insurance.ts';

import { compareGapBounds, isValidGapValue } from '@/utils/duration-utils.ts';

export const MIN_STEPS = 2;
export const MAX_STEPS = 50;

export const GAP_VALUE_ERROR = 'Enter a valid value for the selected unit (Hrs allow only .0 or .5)';
export const GAP_RANGE_ERROR = 'Maximum interval must not be shorter than minimum interval';

const STEPS_MAIN_PRACTITIONER_PATH = ['steps', 'main_practitioner'] as const;
export const STEPS_MAIN_PRACTITIONER_ERROR_KEY = STEPS_MAIN_PRACTITIONER_PATH.join('.');
const MAX_NAME_LENGTH = 120;
const MAX_DESCRIPTION_LENGTH = 500;

const chainStepSchema = z.object({
    order: z.number().int(),
    appointment_type_id: z.string().min(1, 'Appointment type is required'),
    is_main_practitioner: z.boolean().default(false),
});

const chainGapSchema = z
    .object({
        from_step: z.number().int(),
        to_step: z.number().int(),
        min_unit: z.enum(GapUnit),
        min_value: z.number().optional(),
        max_unit: z.enum(GapUnit),
        max_value: z.number().optional(),
    })
    .superRefine((gap, ctx) => {
        if (!isValidGapValue(gap.min_value, gap.min_unit)) {
            ctx.addIssue({ code: 'custom', message: GAP_VALUE_ERROR, path: ['min_value'] });
        }
        if (!isValidGapValue(gap.max_value, gap.max_unit)) {
            ctx.addIssue({ code: 'custom', message: GAP_VALUE_ERROR, path: ['max_value'] });
        }
        // Only compare bounds when both are present; an empty field is already covered by GAP_VALUE_ERROR.
        if (
            gap.min_value !== undefined &&
            gap.max_value !== undefined &&
            compareGapBounds(gap.max_value, gap.max_unit, gap.min_value, gap.min_unit) < 0
        ) {
            ctx.addIssue({ code: 'custom', message: GAP_RANGE_ERROR, path: ['max_value'] });
        }
    });

export const chainConfigFormSchema = z
    .object({
        name: z.object({
            de: z.string().min(1, 'Name (DE) is required').max(MAX_NAME_LENGTH),
            en: z.string().min(1, 'Name (EN) is required').max(MAX_NAME_LENGTH),
        }),
        description: z.object({ de: z.string().max(MAX_DESCRIPTION_LENGTH) }).optional(),
        practice_id: z.string(),
        instance_id: z.string().min(1, 'Instance is required'),
        pvs_provider: z.enum(PVSProvider).optional(),
        insurance_types: z.array(z.enum(InsuranceType)).min(1, 'At least one type of insurance is required.'),
        is_for_new_patients: z.boolean(),
        is_for_returning_patients: z.boolean(),
        is_bookable_by_patient: z.boolean(),
        steps: z.array(chainStepSchema).min(MIN_STEPS, `At least ${MIN_STEPS} steps are required`).max(MAX_STEPS),
        gaps: z.array(chainGapSchema),
    })
    .superRefine((data, ctx) => {
        if (data.pvs_provider === PVSProvider.DOC_CIRRUS && !data.practice_id) {
            ctx.addIssue({
                code: 'custom',
                message: 'Practice location is required',
                path: ['practice_id'],
            });
        }

        // Zod doesn't short-circuit before superRefine — guard against partial validation
        if (data.steps.length < MIN_STEPS) return;

        if (!data.steps.some((s) => s.is_main_practitioner)) {
            ctx.addIssue({
                code: 'custom',
                message: 'At least one step must be marked as Main Practitioner.',
                path: [...STEPS_MAIN_PRACTITIONER_PATH],
            });
        }

        if (data.gaps.length !== data.steps.length - 1) {
            ctx.addIssue({
                code: 'custom',
                message: `Exactly ${data.steps.length - 1} gaps must be defined`,
                path: ['gaps'],
            });
        }

        if (!data.is_for_new_patients && !data.is_for_returning_patients) {
            ctx.addIssue({
                code: 'custom',
                message: 'At least one patient type is required.',
                path: ['is_for_returning_patients'],
            });
        }
    });

export type ChainConfigFormValues = z.infer<typeof chainConfigFormSchema>;
